import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Wrench, AlertTriangle, CheckCircle2, Loader2, ShieldCheck, ScanLine } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

// iter185c — Super-Admin Fund Repair UI.
// Triggers POST /api/super/fund-reversal-repair in dry_run=true (Scan) or
// dry_run=false (Apply) mode and renders the structured drift report.
export default function FundRepairPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [report, setReport] = useState(null);
  const [applyResult, setApplyResult] = useState(null);

  if (!user?.is_super_admin) {
    return (
      <div className="p-6 text-center" data-testid="fund-repair-forbidden">
        <ShieldCheck className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="font-display text-xl font-bold">Super Admin Only</h2>
      </div>
    );
  }

  const runScan = async () => {
    setScanning(true);
    setApplyResult(null);
    try {
      const res = await api.post('/super/fund-reversal-repair', { dry_run: true });
      setReport(res.data);
      const n = res.data.drift_rows_found || 0;
      if (n === 0) {
        toast.success('No drift found — fund is clean.');
      } else {
        toast.warning(`${n} drift row(s) found · ₹${(res.data.total_over_credit || 0).toLocaleString('en-IN')} over-credit`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const runApply = async () => {
    if (!report || (report.drift_rows_found || 0) === 0) {
      toast.error('Run Scan first');
      return;
    }
    const total = report.total_over_credit || 0;
    const confirmMsg = `Apply repair?\n\nThis will reclaim ₹${total.toLocaleString('en-IN')} from ${report.per_group_drift?.length || 0} group fund(s) and write audit-trail rows. Cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    setApplying(true);
    try {
      const res = await api.post('/super/fund-reversal-repair', { dry_run: false });
      setApplyResult(res.data);
      toast.success(res.data.message || 'Repair applied');
      // Re-scan to confirm clean state
      await runScan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const driftCount = report?.drift_rows_found || 0;
  const totalDrift = report?.total_over_credit || 0;

  return (
    <div className="p-6 max-w-2xl lg:max-w-none mx-auto space-y-5" data-testid="fund-repair-page">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/admin')}
          className="p-2 -ml-2 rounded-full hover:bg-muted transition-colors"
          data-testid="fund-repair-back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <Wrench className="w-6 h-6 text-orange-500" /> Fund Repair
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Scan & correct historical fund over-credits on reversed expenses.
          </p>
        </div>
      </div>

      {/* Info Card */}
      <Card className="p-4 border-2 rounded-2xl bg-orange-500/5 border-orange-500/30">
        <div className="flex gap-3">
          <AlertTriangle className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
          <div className="text-sm space-y-1">
            <p className="font-semibold">What does this do?</p>
            <p className="text-muted-foreground">
              An older bug credited the full expense amount back to the Group Fund on reversal — even
              when approval had only partially deducted from the fund. This tool finds those rows and
              writes a corrective <code className="text-xs bg-muted px-1 py-0.5 rounded">fund_adjustment</code>{' '}
              to reclaim the over-credit. Audit-safe, idempotent, no destructive deletes.
            </p>
          </div>
        </div>
      </Card>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <Button
          size="lg"
          variant="outline"
          onClick={runScan}
          disabled={scanning || applying}
          data-testid="fund-repair-scan-btn"
          className="rounded-2xl h-14"
        >
          {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanLine className="w-4 h-4 mr-2" />}
          {scanning ? 'Scanning…' : 'Scan + Show Drift'}
        </Button>
        <Button
          size="lg"
          onClick={runApply}
          disabled={scanning || applying || driftCount === 0}
          data-testid="fund-repair-apply-btn"
          className="rounded-2xl h-14 bg-orange-600 hover:bg-orange-700 disabled:opacity-40"
        >
          {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
          {applying ? 'Applying…' : 'Apply Repair'}
        </Button>
      </div>

      {/* Apply Result */}
      {applyResult && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="p-4 border-2 rounded-2xl bg-green-500/10 border-green-500/40" data-testid="fund-repair-apply-result">
            <div className="flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold">{applyResult.message}</p>
                <p className="text-muted-foreground mt-1">
                  Applied: <span className="font-mono">{applyResult.applied}</span> · Reclaimed:{' '}
                  <span className="font-mono">₹{(applyResult.total_corrected || 0).toLocaleString('en-IN')}</span> ·
                  Groups: <span className="font-mono">{applyResult.groups_corrected}</span>
                </p>
                {applyResult.errors?.length > 0 && (
                  <p className="text-red-500 text-xs mt-2">
                    {applyResult.errors.length} error(s) — check backend logs.
                  </p>
                )}
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Report */}
      {report && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3" data-testid="fund-repair-report">
          {/* Summary */}
          <Card className={`p-4 border-2 rounded-2xl ${driftCount > 0 ? 'border-yellow-500/40 bg-yellow-500/5' : 'border-green-500/40 bg-green-500/5'}`}>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-xs text-muted-foreground">Scanned</p>
                <p className="font-display text-xl font-bold" data-testid="report-scanned">{report.scanned_reversals}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Drift Rows</p>
                <p className="font-display text-xl font-bold text-yellow-600" data-testid="report-drift-count">{driftCount}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Over-credit</p>
                <p className="font-display text-xl font-bold text-red-500" data-testid="report-total-drift">
                  ₹{totalDrift.toLocaleString('en-IN')}
                </p>
              </div>
            </div>
            {report.message && (
              <p className="text-xs text-muted-foreground text-center mt-3 italic">{report.message}</p>
            )}
          </Card>

          {/* Per-group drift */}
          {report.per_group_drift?.length > 0 && (
            <Card className="p-4 border-2 rounded-2xl" data-testid="report-per-group">
              <h3 className="font-semibold text-sm mb-3">Per-Group Drift</h3>
              <div className="space-y-2">
                {report.per_group_drift.map((g) => (
                  <div key={g.group_id} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0">
                    <span className="font-medium truncate">{g.group_name}</span>
                    <span className="font-mono text-red-500 shrink-0 ml-3">
                      ₹{(g.over_credit || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Drift details */}
          {report.drift_details?.length > 0 && (
            <Card className="p-4 border-2 rounded-2xl" data-testid="report-drift-details">
              <h3 className="font-semibold text-sm mb-3">
                Drift Details
                {report.drift_details_truncated && (
                  <span className="ml-2 text-xs text-muted-foreground font-normal">(first 50 shown)</span>
                )}
              </h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {report.drift_details.map((d) => (
                  <div key={d.reversal_id} className="text-xs bg-muted/50 rounded-lg p-2.5 space-y-0.5">
                    <p className="font-semibold truncate">{d.expense_description || '(no description)'}</p>
                    <p className="text-muted-foreground font-mono">
                      Reversed ₹{d.reversal_amount} − Deducted ₹{d.deduct_amount} ={' '}
                      <span className="text-red-500">+₹{d.drift} drift</span>
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Skipped counts */}
          {(report.skipped_no_expense_id > 0 || report.skipped_no_deduct_row > 0) && (
            <Card className="p-3 border rounded-xl bg-muted/30">
              <p className="text-xs text-muted-foreground">
                Skipped: {report.skipped_no_expense_id} row(s) had no expense_id ·{' '}
                {report.skipped_no_deduct_row} row(s) had no matching deduct row.
              </p>
            </Card>
          )}
        </motion.div>
      )}

      {!report && !scanning && (
        <Card className="p-8 border-2 border-dashed rounded-2xl text-center">
          <ScanLine className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Tap <strong>Scan + Show Drift</strong> to begin a dry-run audit.
          </p>
        </Card>
      )}
    </div>
  );
}
